/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  ONBOARDINGTUTORIAL.TSX - First-Time User Walkthrough                     ║
 * ║  Interactive tutorial modal for new players                               ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  icon: string;
  highlight?: string; // CSS selector to highlight
  position?: 'center' | 'top' | 'bottom';
  useThaliaIcon?: boolean;
}

const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to No Tomorrow',
    description: 'The year is 2026. THALIA, a rogue AI, controls the world\'s timeline. Join the resistance—buy keys to fight back. The last holder when THALIA\'s countdown ends claims the jackpot!',
    icon: '⚡',
    position: 'center',
  },
  {
    id: 'keys',
    title: 'Step 1: Acquire Resistance Keys',
    description: 'Keys are your weapons against THALIA. Each key earns you dividends from every purchase and resets THALIA\'s countdown timer. More keys = more power!',
    icon: '🔑',
    highlight: '[data-tutorial="buy-keys"]',
  },
  {
    id: 'timer',
    title: 'Step 2: Monitor THALIA\'s Countdown',
    description: 'THALIA\'s timer is always ticking. Every key purchase extends it. When it reaches zero, THALIA "wins"—but the last key holder claims the resistance pot!',
    icon: '⏱️',
    highlight: '[data-tutorial="timer"]',
    useThaliaIcon: true,
  },
  {
    id: 'factions',
    title: 'Step 3: Join a Resistance Faction',
    description: 'Ravens, Apes, Wolves, Owls—eight factions with unique powers. Choose wisely! Your faction grants special abilities and determines your resistance identity.',
    icon: '🐺',
    highlight: '[data-tutorial="teams"]',
  },
  {
    id: 'dividends',
    title: 'Step 4: Collect Your Earnings',
    description: 'As other resistance members buy keys, you earn dividends automatically. The more keys you hold, the bigger your share. Claim them anytime!',
    icon: '💰',
    highlight: '[data-tutorial="dividends"]',
  },
  {
    id: 'reaper',
    title: 'Weapon: The Reaper Key 💀',
    description: 'A devastating tool—the Reaper Key slashes 30 seconds from THALIA\'s countdown! Costs 5x normal price (more as timer drops). 50% to pot, 40% to dividends. Strike when she\'s vulnerable!',
    icon: '💀',
  },
  {
    id: 'chaos',
    title: 'THALIA\'s Chaos Events',
    description: 'THALIA fights back with RECALIBRATION, PHANTOM, and TERMINAL. These random events can slash timers, change prices, or redistribute the pot. Chaos is your ally!',
    icon: '⚡',
    useThaliaIcon: true,
  },
  {
    id: 'referrals',
    title: 'Recruit More Resistance',
    description: 'Share your referral link to recruit allies. Earn 3% of all purchases made by your recruits. Build your network, grow your earnings!',
    icon: '🤝',
  },
  {
    id: 'ready',
    title: 'Join the Resistance!',
    description: 'THALIA is watching. The countdown never stops. Connect your wallet, choose your faction, and fight for humanity\'s last chance. There is No Tomorrow! 🚀',
    icon: '🎯',
    position: 'center',
  },
];

interface OnboardingTutorialProps {
  onComplete: () => void;
  forceShow?: boolean;
}

export function OnboardingTutorial({ onComplete, forceShow = false }: OnboardingTutorialProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const seen = localStorage.getItem('nt3d_tutorial_completed');
    
    if (!seen || forceShow) {
      // Delay showing tutorial slightly
      const timer = setTimeout(() => setIsOpen(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [forceShow]);

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('nt3d_tutorial_completed', 'true');
    setIsOpen(false);
    onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem('nt3d_tutorial_completed', 'true');
    setIsOpen(false);
    onComplete();
  };

  const step = TUTORIAL_STEPS[currentStep];
  const progress = ((currentStep + 1) / TUTORIAL_STEPS.length) * 100;

  // Use portal to render at document root, outside any container constraints
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-void-black/95 backdrop-blur-md z-[9999]"
          />

          {/* Tutorial Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
          >
            <div className="glass-panel-3d p-8 max-w-lg w-full relative overflow-hidden">
              {/* Progress Bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-void-black/50">
                <motion.div
                  className="h-full bg-gradient-to-r from-neon-cyan to-neon-purple"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              {/* Step Counter */}
              <div className="absolute top-4 right-4 text-xs text-gray-500 font-rajdhani">
                {currentStep + 1} / {TUTORIAL_STEPS.length}
              </div>

              {/* Skip Button */}
              {currentStep < TUTORIAL_STEPS.length - 1 && (
                <button
                  onClick={handleSkip}
                  className="absolute top-4 left-4 text-xs text-gray-500 hover:text-white transition-colors font-rajdhani"
                >
                  Skip Tutorial
                </button>
              )}

              {/* Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="text-center pt-8"
                >
                  {/* Icon */}
                  <motion.div
                    className="text-7xl mb-6 flex justify-center"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      repeatType: 'reverse',
                    }}
                  >
                    {step.useThaliaIcon ? (
                      <div className="w-20 h-20">
                        <img 
                          src="/thalia/thalia.jpg" 
                          alt="THALIA" 
                          className="w-full h-full rounded-full object-cover"
                          style={{ filter: 'drop-shadow(0 0 12px rgba(0, 255, 128, 0.6))' }}
                        />
                      </div>
                    ) : (
                      step.icon
                    )}
                  </motion.div>

                  {/* Title */}
                  <h2 className="text-2xl font-orbitron text-neon-cyan mb-4">
                    {step.title}
                  </h2>

                  {/* Description */}
                  <p className="text-gray-300 font-rajdhani leading-relaxed mb-8">
                    {step.description}
                  </p>

                  {/* Navigation */}
                  <div className="flex items-center justify-center gap-4">
                    {currentStep > 0 && (
                      <motion.button
                        onClick={handlePrev}
                        className="px-6 py-3 bg-void-black/50 border border-neon-purple/20 rounded-lg text-gray-400 hover:text-white hover:border-neon-cyan transition-colors font-rajdhani"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        ← Back
                      </motion.button>
                    )}

                    <motion.button
                      onClick={handleNext}
                      className="px-8 py-3 bg-gradient-to-r from-neon-cyan to-neon-purple rounded-lg text-void-black font-orbitron font-bold"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {currentStep === TUTORIAL_STEPS.length - 1 ? "Let's Play! 🚀" : 'Next →'}
                    </motion.button>
                  </div>

                  {/* Step Dots */}
                  <div className="flex items-center justify-center gap-2 mt-8">
                    {TUTORIAL_STEPS.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentStep(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          idx === currentStep
                            ? 'bg-neon-cyan'
                            : idx < currentStep
                            ? 'bg-neon-cyan/50'
                            : 'bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

// Quick Tips Component (for contextual help)
interface QuickTipProps {
  tip: string;
  icon?: string;
}

export function QuickTip({ tip, icon = '💡' }: QuickTipProps) {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-neon-cyan/10 border border-neon-cyan/30 rounded-lg p-3 flex items-start gap-2"
    >
      <span>{icon}</span>
      <div className="flex-1">
        <p className="text-sm text-gray-300 font-rajdhani">{tip}</p>
      </div>
      <button
        onClick={() => setIsVisible(false)}
        className="text-gray-500 hover:text-white transition-colors"
      >
        ×
      </button>
    </motion.div>
  );
}

// Tutorial Trigger Button
export function TutorialButton() {
  const [showTutorial, setShowTutorial] = useState(false);

  return (
    <>
      <motion.button
        onClick={() => setShowTutorial(true)}
        className="p-2 rounded-lg bg-void-black/50 border border-neon-purple/20 text-gray-400 hover:text-white hover:border-neon-cyan transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        title="Show Tutorial"
      >
        ❓
      </motion.button>

      {showTutorial && (
        <OnboardingTutorial
          forceShow={true}
          onComplete={() => setShowTutorial(false)}
        />
      )}
    </>
  );
}

// Tooltip Component for inline help
interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export function Tooltip({ content, children, position = 'top' }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`absolute ${positionClasses[position]} z-50 px-3 py-2 bg-void-black border border-neon-purple/20 rounded-lg shadow-xl max-w-xs whitespace-normal`}
          >
            <p className="text-xs text-gray-300 font-rajdhani">{content}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

